// Tape rows from archive calls (CONTRACT "Tape": items 14 px artwork, ticker
// 600, name muted, age). The identity cell tells four things apart and never
// guesses. Named: metadata observed, so the ticker leads and the name is the
// muted second string. Exact contract: a valid address with no metadata, so
// the mono short address is the whole identity (the words "Exact contract"
// cost 90 px per item and said nothing the glyph did not). Unresolved: the
// server could not resolve the message to a contract at all. Invalid: the
// recorded address is not a contract shape although the server marked it
// exact. Rows are ordered newest first (the visible left edge is "latest").
import type { ArchiveCall } from '../api/types';
import { resolveTokenIdentity, shortContract } from '../identity/contract';
import { displayLabel } from '../identity/text';
import type { SourceFamily } from '../ui/PresenceDot';

export type TapeIdentityKind = 'named' | 'exact' | 'unresolved' | 'invalid';

export interface TapeItem {
  readonly id: string;
  readonly kind: TapeIdentityKind;
  // Leading string: the ticker when identity was observed, otherwise the mono
  // short address or the unresolved marker.
  readonly primary: string;
  // Muted second string: the token name, or the reason there is no ticker.
  readonly secondary: string | null;
  readonly mono: boolean;
  readonly imageUrl: string | null;
  readonly symbol: string | null;
  readonly source: string;
  readonly family: SourceFamily;
  readonly provider: ArchiveCall['provider'];
  readonly at: string;
}

const FAMILY: Readonly<Record<ArchiveCall['provider'], SourceFamily>> = {
  telegram: 'telegram',
  discord: 'discord',
  x: 'x',
};

export function tapeItemOf(row: ArchiveCall): TapeItem {
  const identity = resolveTokenIdentity({
    contract: row.contractAddress,
    name: row.coinName,
    symbol: row.ticker,
    contractResolution: row.contractResolution,
  });
  const name = displayLabel(identity.name, '');
  const symbol = displayLabel(identity.symbol, '');
  const base = {
    id: row.id,
    imageUrl: row.imageUrl,
    symbol: identity.symbol,
    source: displayLabel(row.groupLabel, displayLabel(row.sourceLabel, row.provider)),
    family: FAMILY[row.provider] ?? 'market',
    provider: row.provider,
    at: row.callEventAt,
  };
  if (row.contractResolution === 'unresolved') {
    return { ...base, kind: 'unresolved', primary: 'Unresolved', secondary: symbol || name || 'no contract', mono: false };
  }
  if (!identity.contract.isValid) {
    return { ...base, kind: 'invalid', primary: shortContract(row.contractAddress), secondary: 'unverified address', mono: true };
  }
  if (identity.metadataState === 'available' && (symbol || name)) {
    return symbol
      ? { ...base, kind: 'named', primary: symbol, secondary: name || null, mono: false }
      : { ...base, kind: 'named', primary: name, secondary: null, mono: false };
  }
  return { ...base, kind: 'exact', primary: shortContract(row.contractAddress), secondary: null, mono: true };
}

// One token, one place on the strip. Two callers naming the same contract
// minutes apart printed the token twice in a row (measured on the live tape:
// "goats" twice), which reads as a stutter rather than as two observations.
// The key is the chain-scoped contract when the address is one the identity
// layer accepted, and the record id otherwise, so unresolved rows never
// collapse into each other.
function identityKey(row: ArchiveCall): string {
  const address = row.contractAddress.trim().toLowerCase();
  if (row.contractResolution !== 'exact_contract' || address === '') return `record:${row.id}`;
  return `${row.chain}:${address}`;
}

// Newest first, deduplicated by token, oldest copies dropped. The kept row is
// the newest observation, so the age on the strip is the freshest one.
export function tapeItemsOf(rows: ReadonlyArray<ArchiveCall>): ReadonlyArray<TapeItem> {
  const seen = new Set<string>();
  return [...rows]
    .sort((a, b) => Date.parse(b.callEventAt) - Date.parse(a.callEventAt))
    .filter((row) => {
      const key = identityKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(tapeItemOf);
}
